/**
 * StoryCard component.
 *
 * Slides in from the right on mount (300ms ease-out). Presents the narrative
 * of an alert lifecycle: detecting -> turned away or entered.
 *
 * Accessibility:
 *   - aria-live="assertive" on new (detecting) cards to interrupt screen readers
 *   - aria-label on the card with full context
 *   - Close button is keyboard accessible
 *
 * Animation:
 *   - Slide in from right + fade (300ms ease-out) on mount
 *   - Slide out to right + fade (200ms ease-in) on exit via AnimatePresence
 *   - Header color transitions via CSS transition (200ms ease-in-out)
 *   - Under prefers-reduced-motion: no slide/scale, instant appearance
 *
 * Design spec reference: section 4, 4.1, 4.3.
 * Plan 03-06, CL-04.
 */
import { motion } from 'framer-motion';
import { X } from '@phosphor-icons/react';
import type { StoryCardState } from './useStoryCards';
import { CameraFrame } from './CameraFrame';
import { RssiSpark } from './RssiSpark';
import * as styles from './StoryCard.css';

// ---------------------------------------------------------------------------
// Framer Motion variants
// ---------------------------------------------------------------------------

/** Card slide-in from right (desktop). */
const cardVariants = {
  initial: { x: '100%', opacity: 0 },
  animate: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] },
  },
  exit: {
    x: '100%',
    opacity: 0,
    transition: { duration: 0.2, ease: [0.7, 0, 0.84, 0] },
  },
};

/** Reduced-motion variant: fade only, no slide. */
const cardVariantsReduced = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.15 } },
  exit: { opacity: 0, transition: { duration: 0.1 } },
};

/** Resolution text fade-replace transition. */
const outcomeVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.2, ease: [0.65, 0, 0.35, 1] } },
  exit: { opacity: 0, transition: { duration: 0.1 } },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Returns the CSS class for the card header band based on alert state.
 *
 * @param state - Current card state
 * @returns vanilla-extract class name
 */
function headerClass(state: StoryCardState['state']): string {
  switch (state) {
    case 'turned-away': return styles.headerTurnedAway;
    case 'entered': return styles.headerEntered;
    case 'ambiguous': return styles.headerAmbiguous;
    default: return styles.headerDetecting;
  }
}

/**
 * Returns the CSS class for the outcome dot based on card state.
 *
 * @param state - Current card state
 * @returns vanilla-extract class name
 */
function dotClass(state: StoryCardState['state']): string {
  switch (state) {
    case 'turned-away': return styles.outcomeDotTurnedAway;
    case 'entered': return styles.outcomeDotEntered;
    case 'ambiguous': return styles.outcomeDotAmbiguous;
    default: return styles.outcomeDotDetecting;
  }
}

/**
 * Returns outcome text for display.
 *
 * @param card - Current story card state
 * @returns Human-readable outcome string
 */
function outcomeText(card: StoryCardState): string {
  if (card.resolutionText) return card.resolutionText;
  return 'Detecting...';
}

/**
 * Formats alert_type + zone for the header.
 *
 * @param alertType - Canonical alert type string
 * @param zoneName - Zone name or null
 * @returns Formatted header string, e.g. "APPROACH - Zone 2 - Threshold"
 */
function headerTitle(alertType: string, zoneName: string | null): string {
  const type = alertType.toUpperCase().replace(/_/g, ' ');
  if (!zoneName) return type;
  return `${type} - ${zoneName}`;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  /** Story card state from useStoryCards. */
  card: StoryCardState;
  /** Called when the user clicks the X button. */
  onDismiss: (alertId: number) => void;
  /** Called when the user clicks anywhere on the card body. */
  onCardClick: (alertId: number) => void;
  /** Whether the user prefers reduced motion. */
  reducedMotion: boolean;
  /**
   * Seconds remaining until auto-dismiss (for turned-away cards).
   * Shown as a small countdown ring on the close button.
   * Null when no countdown is active.
   */
  secondsUntilDismiss: number | null;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * StoryCard: a single alert narrative card with lifecycle animations.
 *
 * @param props - Card, dismiss, click, reducedMotion, secondsUntilDismiss
 */
export function StoryCard({
  card,
  onDismiss,
  onCardClick,
  reducedMotion,
  secondsUntilDismiss,
}: Props) {
  const variants = reducedMotion ? cardVariantsReduced : cardVariants;
  const showCountdown =
    secondsUntilDismiss !== null && card.state === 'turned-away';

  // Circumference for the countdown ring SVG circle (r=10).
  const CIRC = 2 * Math.PI * 10;
  const progress =
    showCountdown && secondsUntilDismiss !== null
      ? 1 - secondsUntilDismiss / 30
      : 0;
  const dashOffset = CIRC * (1 - progress);

  return (
    <motion.article
      className={styles.card}
      variants={variants}
      initial="initial"
      animate="animate"
      exit="exit"
      aria-live={card.state === 'detecting' ? 'assertive' : 'polite'}
      aria-label={`Alert: ${headerTitle(card.alertType, card.zoneName)}. Status: ${outcomeText(card)}`}
      onClick={() => onCardClick(card.alertId)}
      role="article"
    >
      {/* Header */}
      <div className={headerClass(card.state)}>
        <span className={styles.headerTitle}>
          {headerTitle(card.alertType, card.zoneName)}
        </span>
        <button
          className={styles.closeButton}
          aria-label="Dismiss alert card"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(card.alertId);
          }}
        >
          {/* Countdown ring SVG overlay for auto-dismiss */}
          {showCountdown && (
            <svg className={styles.countdownRing} viewBox="0 0 24 24" aria-hidden="true">
              <circle
                cx="12"
                cy="12"
                r="10"
                fill="none"
                stroke="var(--colors-status-ok)"
                strokeWidth="1.5"
                strokeDasharray={CIRC}
                strokeDashoffset={dashOffset}
                strokeLinecap="round"
                transform="rotate(-90 12 12)"
              />
            </svg>
          )}
          <X size={14} weight="bold" />
        </button>
      </div>

      {/* Fingerprint ID */}
      {card.fingerprint && (
        <div className={styles.fingerprint} aria-label="Device fingerprint">
          {card.fingerprint}
        </div>
      )}

      {/* Card body */}
      <div className={styles.body}>
        {/* Camera frame */}
        <CameraFrame cameraUrl={card.cameraUrl} />

        {/* RSSI sparkline */}
        <RssiSpark />

        {/* Outcome area */}
        <motion.div
          key={card.state}
          className={styles.outcomeArea}
          variants={outcomeVariants}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          <span className={dotClass(card.state)} aria-hidden="true" />
          <span className={styles.outcomeText}>{outcomeText(card)}</span>
        </motion.div>

        {/* View full event link */}
        <span className={styles.viewLink} aria-hidden="true">
          View full event
        </span>
      </div>
    </motion.article>
  );
}
